import React from 'react';
import { ArrowDownOutlined, ArrowUpOutlined } from '@ant-design/icons';

export type TrendDirection = 'up' | 'down' | 'same' | 'none';

interface TrendIndicatorProps {
    trend: TrendDirection;
    size?: 'small' | 'medium' | 'large';
    showLabel?: boolean;
    className?: string;
    style?: React.CSSProperties;
    positiveIsGood?: boolean;
    iconSize?: number;
    textFontSize?: number;
}

/**
 * Reusable component for displaying trend indicators (arrows, equals, dash)
 * Used across Dashboard and MeasurementSummaryCard
 */
const TrendIndicator: React.FC<TrendIndicatorProps> = ({
    trend,
    size = 'medium',
    showLabel = false,
    className = '',
    style = {},
    positiveIsGood = true,
    iconSize,
    textFontSize,
}) => {
    const positiveColor = '#52c41a';
    const negativeColor = '#ff4d4f';
    const effectivePositiveIsGood = positiveIsGood !== undefined ? positiveIsGood : true;
    const iconSizeMap = {
        small: 16,
        medium: 18,
        large: 20,
    } as const;
    const iconFontSize = (iconSize ?? iconSizeMap[size]) || iconSizeMap.medium;
    const textSize = textFontSize ?? iconFontSize;
    const iconStyle = { fontSize: iconFontSize };

    const getIcon = () => {
        switch (trend) {
            case 'up':
                return (
                    <ArrowUpOutlined
                        style={{
                            ...iconStyle,
                            color: effectivePositiveIsGood ? positiveColor : negativeColor,
                        }}
                    />
                );
            case 'down':
                return (
                    <ArrowDownOutlined
                        style={{
                            ...iconStyle,
                            color: effectivePositiveIsGood ? negativeColor : positiveColor,
                        }}
                    />
                );
            case 'same':
                return <span style={{ fontWeight: 700, fontSize: textSize }}>=</span>;
            case 'none':
                return <span style={{ opacity: 0.6, fontSize: textSize }}>-</span>;
            default:
                return null;
        }
    };

    const getLabel = () => {
        switch (trend) {
            case 'up':
                return 'Increasing';
            case 'down':
                return 'Decreasing';
            case 'same':
                return 'No change';
            case 'none':
                return 'No data';
            default:
                return '';
        }
    };

    const sizeStyles = {
        small: { fontSize: '12px' },
        medium: { fontSize: '14px' },
        large: { fontSize: '14px' },
    };

    return (
        <span 
            className={`trend-indicator ${className}`}
            style={{ ...sizeStyles[size], ...style }}
            title={getLabel()}
        >
            {getIcon()}
        {showLabel && (
            <span style={{ marginLeft: 4, fontSize: textSize }}>
                {getLabel()}
            </span>
        )}
        </span>
    );
};

export default TrendIndicator;
